import React, { useState } from 'react';
import config from '../../config/config';

const API_BASE = config.api.baseUrl;

/**
 * "הצטרפות לשירות הוואטסאפ" — pick one of the record's phones and send the approved opt-in
 * template (customer_contact_permission_v2) through /api/whatsapp/send-template.
 *
 * Moved out of App.js (the Account page's modal) unchanged, so the leads panel uses the very
 * same one (owner, 2026-10-29). The caller passes the phones it has ([{field, label, value}])
 * and the customer's first name; the modal mounts fresh each time it opens.
 */
export default function WhatsAppOptInModal({ phones, firstName, currentUser, emptyText = 'לא נמצאו מספרי טלפון בחשבון', onClose }) {
  const [selectedField, setSelectedField] = useState(null);
  const [selectedValue, setSelectedValue] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // null | 'success' | 'error'
  const [error, setError] = useState('');

  const clean = (v) => (v && v !== 'nan' && v !== 'null' && v !== 'undefined') ? String(v).trim() : '';
  const phoneOptions = phones.map(p => ({ ...p, value: clean(p.value) }));
  const anyAvailable = phoneOptions.some(o => o.value);
  const normalize = (p) => {
    if (!p) return null;
    let c = p.replace(/[^0-9]/g, '');
    if (c.startsWith('05') && c.length === 10) c = '972' + c.substring(1);
    else if (c.startsWith('0') && c.length === 9) c = '972' + c.substring(1);
    else if (!c.startsWith('972') && c.length === 9) c = '972' + c;
    return c;
  };
  // 2026-06-15 owner-spec: 'customer_contact_permission' — a UTILITY template on the 972549954599
  // sender; 2026-06-21 bumped to '_v2'. Single body placeholder: the customer's first name.
  const firstNameForTemplate = clean(firstName) || 'לקוח';
  // the rendered body — the modal's preview and the send's display_text, so the chat shows the
  // real message instead of "[Template: customer_contact_permission]"
  const optInDisplayText = `שלום ${firstNameForTemplate},\n\nבהמשך לפנייתך לשירות הלקוחות של אפיקים, נבקש את אישורך להמשיך את הטיפול בפנייתך באמצעות WhatsApp.\n\nהאם אתה מאשר?`;
  const sendOptIn = async () => {
    if (!selectedValue) return;
    setSending(true);
    setError('');
    try {
      const phone = normalize(selectedValue);
      const res = await fetch(`${API_BASE}/api/whatsapp/send-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone,
          template_name: 'customer_contact_permission_v2',
          language: 'he',
          placeholders: [firstNameForTemplate],
          display_text: optInDisplayText,
          userId: currentUser?.id || '',
          userName: `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || currentUser?.username || ''
        })
      });
      const data = await res.json().catch(() => ({ success: false, error: 'שגיאת תקשורת' }));
      if (data.success) {
        setResult('success');
      } else {
        setResult('error');
        setError(data.error || 'שגיאה לא ידועה');
      }
    } catch (e) {
      setResult('error');
      setError(e.message || String(e));
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="slds-backdrop slds-backdrop_open" style={{ zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div role="dialog" aria-label="הצטרפות לשירות הוואטסאפ" style={{ background: '#fff', borderRadius: '8px', minWidth: '380px', maxWidth: '520px', padding: '24px', direction: 'rtl', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #e8eaed', paddingBottom: '12px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', color: '#16325c' }}>💬 הצטרפות לשירות הוואטסאפ</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#54698d' }}>✕</button>
        </div>

        {result === 'success' ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#2e844a', marginBottom: '20px' }}>הודעה נשלחה ללקוח</div>
            <button onClick={onClose} style={{ padding: '10px 24px', background: '#0176d3', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}>סגור</button>
          </div>
        ) : result === 'error' ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>❌</div>
            <div style={{ fontSize: '15px', color: '#c23934', marginBottom: '20px' }}>שגיאה בשליחת ההודעה</div>
            <div style={{ fontSize: '13px', color: '#54698d', marginBottom: '20px', padding: '8px', background: '#fef1ed', borderRadius: '4px' }}>{error}</div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <button onClick={() => { setResult(null); setError(''); }} style={{ padding: '8px 16px', background: '#f4f6f9', border: '1px solid #d8dde6', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>נסה שוב</button>
              <button onClick={onClose} style={{ padding: '8px 16px', background: '#fff', border: '1px solid #d8dde6', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>סגור</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '14px', fontSize: '13px', color: '#3e3e3c' }}>בחר מספר טלפון לשליחת הודעת ההצטרפות:</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              {phoneOptions.map((opt) => {
                const isSelected = selectedField === opt.field;
                const isEmpty = !opt.value;
                return (
                  <label key={opt.field} style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 12px', borderRadius: '6px',
                    border: `1px solid ${isSelected ? '#0176d3' : '#d8dde6'}`,
                    background: isSelected ? '#eef4fb' : '#fff',
                    cursor: isEmpty ? 'default' : 'pointer',
                    opacity: isEmpty ? 0.6 : 1,
                  }}>
                    <input
                      type="radio"
                      name="wa-opt-in-phone"
                      checked={isSelected}
                      disabled={isEmpty}
                      onChange={() => { setSelectedField(opt.field); setSelectedValue(opt.value); }}
                    />
                    <div style={{ flex: 1, textAlign: 'right' }}>
                      <div style={{ fontSize: '12px', color: '#54698d' }}>{opt.label}</div>
                      <div style={{ fontSize: '15px', direction: 'ltr', textAlign: 'right', color: isEmpty ? '#a0a0a0' : '#16325c', fontWeight: 500 }}>
                        {opt.value || '— ריק —'}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            {!anyAvailable && (
              <div style={{ marginBottom: '14px', padding: '8px 12px', background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: '6px', color: '#e65100', fontSize: '12px', textAlign: 'center' }}>
                {emptyText}
              </div>
            )}

            <div style={{ background: '#f4f6f9', padding: '10px 12px', borderRadius: '4px', marginBottom: '16px', fontSize: '12px', color: '#54698d' }}>
              <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>תוכן ההודעה:</div>
              <div style={{ direction: 'rtl', whiteSpace: 'pre-wrap' }}>{optInDisplayText}</div>
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={onClose}
                disabled={sending}
                style={{ padding: '8px 18px', background: '#fff', border: '1px solid #d8dde6', borderRadius: '4px', cursor: sending ? 'not-allowed' : 'pointer', fontSize: '13px', color: '#16325c' }}
              >
                ביטול
              </button>
              <button
                onClick={sendOptIn}
                disabled={!selectedValue || sending}
                style={{ padding: '8px 22px', background: (!selectedValue || sending) ? '#ccc' : '#25D366', color: '#fff', border: 'none', borderRadius: '4px', cursor: (!selectedValue || sending) ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 'bold' }}
              >
                {sending ? 'שולח...' : 'שליחה'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
